import { useSessionKey } from "@auth/sessionStore";
import type {
  CaptureSegment,
  RoomCaptureTimeline,
  VisitPresence,
} from "@capture/captureClient";
import { useCaptureTimeline } from "@capture/captureQueries";
import { EmptyNote, LabelTape, Swipe } from "@scribenote/brian-design-system";
import { formatTime } from "@utils/format-date";
import { useElementWidth } from "@utils/use-element-width";
import { useNow } from "@utils/use-now";
import { addHours } from "date-fns";
import { useState } from "react";

// `hollow` is the lightest ink clearing 3:1 on paper. A ring, not a border: a
// border would shift the box bars resolve percentages against vs the now line.
const TRACK = "inset-ring-1 inset-ring-hollow bg-recess";

// Opaque `hollow` is the lightest grid ink clearing 3:1 on the empty track; it
// falls below 3:1 over the presence wash by design.
const HAIRLINE = "border-hollow";

// The wash alone is 1.21:1 on the track, so PRESENCE_EDGE (>=3.72:1 on every
// ground) draws the shape. Shared with the legend swatch.
const PRESENCE_FILL =
  "color-mix(in srgb, var(--color-accent) 16%, transparent)";
const PRESENCE_FILL_HOVER =
  "color-mix(in srgb, var(--color-accent) 30%, transparent)";
// Ink only; sides are the call site's. The legend swatch matches a closed bar.
const PRESENCE_EDGE = "border-accent-deep";

const TICK_STEPS_HOURS = [1, 2, 3, 4, 6, 12];
// Fits a "12:00 AM" label plus a gap.
const MIN_TICK_PX = 88;

const tickStepHours = (span: number, width: number | null): number =>
  width === null
    ? 2
    : (TICK_STEPS_HOURS.find(
        (h) => (width * h * 3_600_000) / span >= MIN_TICK_PX,
      ) ?? 12);

// `w-36` label column + `gap-2`, as a raw length for overlays spanning rows.
const LABEL_COL_REM = "9.5rem";

const makePct =
  (windowStart: number, span: number) =>
  (t: number): number =>
    ((t - windowStart) / span) * 100;

const hourTicks = (
  windowStart: number,
  windowEnd: number,
  stepHours: number,
): number[] => {
  let tick = new Date(windowStart);
  tick.setMinutes(0, 0, 0);
  if (tick.getTime() < windowStart) tick = addHours(tick, 1);

  const ticks: number[] = [];
  for (let t = tick; t.getTime() <= windowEnd; t = addHours(t, stepHours)) {
    ticks.push(t.getTime());
  }
  return ticks;
};

const isFlowing = (
  s: CaptureSegment,
): s is CaptureSegment & { kind: "flowing" } => s.kind === "flowing";

/** Union of overlapping stays (multi-pet visits share a room), so bars never stack. */
interface PresenceBlock {
  start: number;
  end: number;
  openEnded: boolean;
  stays: VisitPresence[];
}

const presenceBlocks = (
  stays: VisitPresence[],
  windowStart: number,
  windowEnd: number,
): PresenceBlock[] => {
  const intervals = stays
    .map((stay) => ({
      // Stays arrive un-clamped.
      start: Math.max(new Date(stay.enteredAt).getTime(), windowStart),
      end: stay.leftAt
        ? Math.min(new Date(stay.leftAt).getTime(), windowEnd)
        : windowEnd,
      openEnded: stay.leftAt === null,
      stay,
    }))
    .filter((i) => i.end > i.start)
    .sort((a, b) => a.start - b.start);

  const blocks: PresenceBlock[] = [];
  for (const interval of intervals) {
    const last = blocks[blocks.length - 1];
    if (last && interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
      last.openEnded = last.openEnded || interval.openEnded;
      last.stays.push(interval.stay);
    } else {
      blocks.push({
        start: interval.start,
        end: interval.end,
        openEnded: interval.openEnded,
        stays: [interval.stay],
      });
    }
  }
  return blocks;
};

const presenceLines = (block: PresenceBlock): string[] =>
  block.stays.map(
    (stay) =>
      `${stay.patientName ?? "Unknown patient"} ${formatTime(stay.enteredAt)} – ${
        stay.leftAt ? formatTime(stay.leftAt) : "still in room"
      }`,
  );

/** Row-level because the track is `overflow-hidden`; clamped off the page edges. */
const BarTooltip = ({
  fraction,
  lines,
}: {
  fraction: number;
  lines: string[];
}) => {
  const clamped = Math.min(Math.max(fraction, 0.06), 0.94);
  return (
    <div
      className="pointer-events-none absolute bottom-full z-dropdown mb-1 -translate-x-1/2 rounded-paper border border-rule bg-card px-2 py-1 text-xs whitespace-nowrap text-ink shadow-float"
      style={{
        left: `calc(${LABEL_COL_REM} + (100% - ${LABEL_COL_REM}) * ${clamped.toFixed(4)})`,
      }}
    >
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
};

type HoveredBar = { kind: "presence" | "flowing"; index: number } | null;

const containsNow = (segments: CaptureSegment[], now: number): boolean =>
  segments.some(
    (s) =>
      isFlowing(s) &&
      new Date(s.start).getTime() <= now &&
      now <= new Date(s.end).getTime(),
  );

export const CaptureTimeline = ({
  date,
  isToday,
}: {
  date: string;
  /** Computed in clinic tz. */
  isToday: boolean;
}) => {
  const sessionKey = useSessionKey();
  const { data } = useCaptureTimeline(sessionKey, date, isToday);

  // Labels use the browser timezone, not the clinic's.
  const windowStart = new Date(data.windowStart).getTime();
  const windowEnd = new Date(data.windowEnd).getTime();
  const span = windowEnd - windowStart;

  const now = useNow();
  const [tickRowRef, tickRowWidth] = useElementWidth();
  const nowInWindow = isToday && now >= windowStart && now <= windowEnd;

  if (span <= 0) {
    return <EmptyNote>No timeline for this day.</EmptyNote>;
  }

  if (data.rooms.length === 0) {
    return <EmptyNote>No active rooms.</EmptyNote>;
  }

  const pct = makePct(windowStart, span);
  const ticks = hourTicks(
    windowStart,
    windowEnd,
    tickStepHours(span, tickRowWidth),
  );
  // `calc()` allows length * number, not percentage * percentage.
  const nowLineLeft = `calc(${LABEL_COL_REM} + (100% - ${LABEL_COL_REM}) * ${(
    pct(now) / 100
  ).toFixed(6)})`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <div className="w-36 shrink-0" />
        <div ref={tickRowRef} className="relative h-5 flex-1">
          {ticks.map((tick) => {
            const left = pct(tick);
            // Edge ticks align inward so labels never force a horizontal scroll.
            const align =
              left < 2
                ? ""
                : left > 98
                  ? "-translate-x-full"
                  : "-translate-x-1/2";
            return (
              <span
                key={tick}
                style={{ left: `${left}%` }}
                className={`absolute font-mono text-fine-print whitespace-nowrap text-annotation ${align}`}
              >
                {formatTime(new Date(tick).toISOString())}
              </span>
            );
          })}
        </div>
      </div>

      <div className="relative flex flex-col gap-2">
        {nowInWindow && (
          <div
            data-testid="capture-now-line"
            className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-ink-stopped"
            style={{ left: nowLineLeft }}
          />
        )}
        {data.rooms.map((room) => (
          <RoomRow
            key={room.roomUuid}
            room={room}
            pct={pct}
            ticks={ticks}
            now={now}
            nowInWindow={nowInWindow}
            windowStart={windowStart}
            windowEnd={windowEnd}
          />
        ))}
      </div>

      <div className="flex items-center gap-4 text-xs text-annotation">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-[1px] bg-ink-working" />
          Flowing
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`size-2.5 rounded-[1px] ${TRACK}`} />
          Gap
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className={`size-2.5 rounded-[1px] border-x ${PRESENCE_EDGE}`}
            style={{ background: PRESENCE_FILL }}
          />
          In room
        </span>
      </div>
    </div>
  );
};

const RoomRow = ({
  room,
  pct,
  ticks,
  now,
  nowInWindow,
  windowStart,
  windowEnd,
}: {
  room: RoomCaptureTimeline;
  pct: (t: number) => number;
  ticks: number[];
  now: number;
  nowInWindow: boolean;
  windowStart: number;
  windowEnd: number;
}) => {
  const live = nowInWindow && containsNow(room.segments, now);
  const presence = presenceBlocks(room.stays, windowStart, windowEnd);
  const [hovered, setHovered] = useState<HoveredBar>(null);
  const flowing = room.segments.filter(isFlowing);

  const hoveredBlock =
    hovered?.kind === "presence" ? presence[hovered.index] : undefined;
  const hoveredSegment =
    hovered?.kind === "flowing" ? flowing[hovered.index] : undefined;

  return (
    <div
      data-testid={`capture-timeline-row-${room.roomUuid}`}
      className="relative flex items-center gap-2"
    >
      {hoveredBlock && (
        <BarTooltip
          fraction={(pct(hoveredBlock.start) + pct(hoveredBlock.end)) / 200}
          lines={presenceLines(hoveredBlock)}
        />
      )}
      {hoveredSegment && (
        <BarTooltip
          fraction={
            (pct(new Date(hoveredSegment.start).getTime()) +
              pct(new Date(hoveredSegment.end).getTime())) /
            200
          }
          lines={[
            `Flowing ${formatTime(hoveredSegment.start)} – ${formatTime(hoveredSegment.end)}`,
          ]}
        />
      )}
      <div className="flex w-36 shrink-0 items-center gap-1.5">
        {/* Free-text names truncate rather than push out the live marker. */}
        <LabelTape className="min-w-0 truncate">{room.roomName}</LabelTape>
        {live && (
          <Swipe
            tone="working"
            className="shrink-0 text-fine-print"
            data-testid={`capture-live-badge-${room.roomUuid}`}
          >
            Live
          </Swipe>
        )}
      </div>
      <div
        className={`relative h-6 flex-1 overflow-hidden rounded-paper ${TRACK}`}
      >
        {presence.map((block, i) => {
          const start = pct(block.start);
          const end = pct(block.end);
          const isHovered = hovered?.kind === "presence" && hovered.index === i;
          const color = isHovered ? PRESENCE_FILL_HOVER : PRESENCE_FILL;
          return (
            <div
              key={`${block.start}-${i}`}
              data-testid={`capture-presence-${room.roomUuid}-${i}`}
              onMouseEnter={() => setHovered({ kind: "presence", index: i })}
              onMouseLeave={() => setHovered(null)}
              // Open-ended stays get no end edge; the fade says "no known end".
              className={`absolute inset-y-0 transition-colors ${PRESENCE_EDGE} ${
                block.openEnded ? "border-l" : "border-x"
              }`}
              style={{
                left: `${start}%`,
                width: `${end - start}%`,
                background: block.openEnded
                  ? `linear-gradient(to right, ${color} 60%, transparent)`
                  : color,
              }}
            />
          );
        })}
        {ticks.map((tick) => (
          <div
            key={tick}
            style={{ left: `${pct(tick)}%` }}
            className={`pointer-events-none absolute inset-y-0 border-l ${HAIRLINE}`}
          />
        ))}
        {flowing.map((segment, i) => {
          const start = pct(new Date(segment.start).getTime());
          const end = pct(new Date(segment.end).getTime());
          const isHovered = hovered?.kind === "flowing" && hovered.index === i;
          return (
            <div
              key={`${segment.start}-${segment.end}`}
              onMouseEnter={() => setHovered({ kind: "flowing", index: i })}
              onMouseLeave={() => setHovered(null)}
              className={`absolute inset-y-0 bg-ink-working transition-[filter] ${isHovered ? "brightness-110" : ""}`}
              style={{ left: `${start}%`, width: `${end - start}%` }}
            />
          );
        })}
      </div>
    </div>
  );
};
